import { BadInputError } from '@watershed/errors/BadInputError';

import { ASTCall } from '../ast';
import { Context, Func, SqlExpression } from '../types';
import { ExprType, stringifyType } from '../expr_types';
import {
  assertArgShape,
  assertKwargShape,
  typecheckExpression,
  isSameType,
  TypecheckWarning,
} from '../typecheck';
import { evalExpression } from '../eval';
import { exprToSql } from '../sql';
import { safeMaxStage } from '../utils';
import { mergeErrors } from '../sql/internal';

// Function for checking list membership. Syntax:
// list_contains(list(1, 2, 3), 1)
export const list_contains: Func = {
  typecheck(
    ctx: Context,
    call: ASTCall,
    warnings?: Array<TypecheckWarning>
  ): ExprType {
    assertArgShape(call, 2, 2);
    assertKwargShape(call, [], false, undefined, undefined);

    // First argument must be a list.
    const listTy = typecheckExpression(ctx, call.args[0], warnings);
    if (listTy.ty !== 'list') {
      throw new BadInputError(
        `First argument of list_contains must be a list, got ${stringifyType(
          listTy
        )}`
      );
    }

    // Second argument must match the element type.
    const needleTy = typecheckExpression(ctx, call.args[1], warnings);
    if (!isSameType(needleTy, listTy.elemTy)) {
      throw new BadInputError(
        `Second argument of list_contains must match element type of the list, got ${stringifyType(
          needleTy
        )} vs ${stringifyType(listTy.elemTy)}`
      );
    }

    return { ty: 'boolean' };
  },

  eval(ctx: Context, call: ASTCall): unknown {
    const vals = call.args.map((arg) => evalExpression(ctx, arg));
    return (vals[0] as Array<unknown>).includes(vals[1]);
  },

  toSql(ctx: Context, call: ASTCall): SqlExpression {
    const argsSql = call.args.map((arg) => exprToSql(ctx, arg));
    return {
      expr: `list_contains(${argsSql.map((a) => a.expr).join(',')})`,
      stage: safeMaxStage(argsSql),
      unitExpr: null,
      error: mergeErrors(argsSql.map((a) => a.error)),
      trace: null,
    };
  },
};
